import { motion } from "framer-motion";
import { theme } from "../../theme.js";
import { techIcons } from "../../data/techInfo";

const frontendSkills = ["React", "Next.js", "TypeScript", "JavaScript", "Tailwind CSS", "Redux", "HTML5", "CSS3"];
const backendSkills = ["Node.js", "Express", "MongoDB", "GraphQL"];
const toolsSkills = ["Git", "VS Code", "Photoshop", "Illustrator"];

const Skills = () => {
  const renderSkillIcons = (skillNames) => {
    return skillNames.map(name => {
      const skill = techIcons.find(t => t.name === name);
      if (!skill) return null;
      return (
        <div key={name} className="flex flex-col items-center gap-2 group">
          <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 flex items-center justify-center p-3 group-hover:bg-cyan-500/10 group-hover:border-cyan-500/30 transition-all duration-300">
            <img src={`/assets/images/${skill.img}`} alt={name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300" />
          </div>
          <span className="text-xs text-zinc-600 dark:text-zinc-500 group-hover:text-zinc-900 dark:group-hover:text-zinc-300 transition-colors heading-font">{name}</span>
        </div>
      );
    });
  };

  return (
    <section id="skills" className="relative py-12 md:py-32 bg-zinc-50 dark:bg-black overflow-hidden body-font">
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-10 md:mb-16"
        >
          <h2 className="text-4xl md:text-7xl font-bold mb-6 heading-font tracking-tighter text-zinc-900 dark:text-white">
            TECH <span className="text-zinc-300 dark:text-zinc-700">STACK</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[minmax(180px,auto)]">
          {/* Frontend Bento Box (Spans 2 columns) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="md:col-span-2 glass-panel p-8 md:p-10 rounded-3xl relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 blur-[80px] rounded-full pointer-events-none group-hover:bg-cyan-500/20 transition-colors duration-700" />
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-8 heading-font">Frontend Engineering</h3>
            <div className="flex flex-wrap gap-6 md:gap-8">
              {renderSkillIcons(frontendSkills)}
            </div>
          </motion.div>

          {/* Backend Bento Box */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="glass-panel p-8 md:p-10 rounded-3xl relative overflow-hidden group"
          >
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none group-hover:bg-indigo-500/20 transition-colors duration-700" />
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-8 heading-font">Backend</h3>
            <div className="flex flex-wrap gap-6">
              {renderSkillIcons(backendSkills)}
            </div>
          </motion.div>

          {/* Tools & Design Bento Box */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="md:col-span-2 lg:col-span-1 glass-panel p-8 md:p-10 rounded-3xl relative overflow-hidden group"
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/10 blur-[80px] rounded-full pointer-events-none group-hover:bg-purple-500/20 transition-colors duration-700" />
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-8 heading-font">Tools & Design</h3>
            <div className="flex flex-wrap gap-6">
              {renderSkillIcons(toolsSkills)}
            </div>
          </motion.div>

          {/* Text/Info Bento Box */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="md:col-span-2 glass-panel p-8 md:p-10 rounded-3xl flex items-center justify-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-100 to-zinc-50 dark:from-zinc-900 dark:to-black z-0" />
            <p className="text-xl md:text-3xl text-zinc-600 dark:text-zinc-400 font-light leading-relaxed heading-font z-10 max-w-2xl text-center relative">
              Constantly exploring new technologies to build <span className="text-zinc-900 dark:text-white font-medium">faster, scalable, and more beautiful</span> digital experiences.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
